import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
    DateUtilities,
    FormatDurationPipe,
    selectCurrentDayDurationInMinutes,
    selectMaxWeeklyWorkingHours,
    selectTotalWeekCommittedInMinutes,
    selectTotalWeekDurationInMinutes,
    selectTotalWeekInProgressInMinutes,
    selectWeeklyWorkdays,
    StatusBar,
    WeeklyWorkdayModel,
} from '@evorait/pace-core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { getStatusBarDetails } from '../weekly-work-time-status-bar';

/**
 * Store-connected replacement for `WeeklyWorkTimeComponent`, routed behind the
 * `environment.enableNgRx` flag.
 *
 * Reads {@link selectWeeklyWorkdays} and its derived totals straight off the store through
 * `Store.selectSignal` — the NgRx migration for this domain is complete, so there is no interface
 * or service left standing between the component and the store.
 *
 * Unlike the legacy panel, it shows work that has not yet been confirmed separately from
 * committed work: each row's `(+HH:MM)` marker is the day's in-progress hours, and the header
 * total is the committed week total plus a marker for the week's total in-progress hours. Both
 * totals still sum committed and in-progress for the exceeded-hours highlight and the status bar,
 * exactly like the legacy component — the split is purely additive to what is already shown.
 *
 * Durations are read and shown in minutes, the unit the selectors count in; only the configured
 * weekly limit is in hours, so it is converted once for the comparisons.
 */
@Component({
    selector: 'pace-weekly-work-time-ngrx',
    templateUrl: './weekly-work-time-ngrx.component.html',
    styleUrls: ['./weekly-work-time-ngrx.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class WeeklyWorkTimeNgrxComponent {
    public readonly isWorkStartedForToday = input.required<boolean>();

    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);
    private readonly formatDuration = inject(FormatDurationPipe);

    public readonly maxWorkingHours = this.store.selectSignal(selectMaxWeeklyWorkingHours);
    private readonly maxWorkingMinutes = computed(() => moment.duration(this.maxWorkingHours(), 'hours').asMinutes());

    /** Title/aria-label of the `(+HH:MM)` in-progress marker. Resolved once — it never changes. */
    public readonly inProgressLabel = <string>this.translate.instant('WEEKLY_WORK_IN_PROGRESS_LABEL');

    public readonly weeklyWorkdays = this.store.selectSignal(selectWeeklyWorkdays);

    /** Committed minutes across the projected week — the header's primary total. */
    public readonly totalCommittedInMinutes = this.store.selectSignal(selectTotalWeekCommittedInMinutes);
    /** In-progress minutes across the projected week — the header's `(+HH:MM)` marker. */
    public readonly totalInProgressInMinutes = this.store.selectSignal(selectTotalWeekInProgressInMinutes);

    private readonly totalWeekDurationInMinutes = this.store.selectSignal(selectTotalWeekDurationInMinutes);
    private readonly currentDayDurationInMinutes = this.store.selectSignal(selectCurrentDayDurationInMinutes);

    /** The status bar for today, recomputed whenever today's hours or the started flag change. */
    public readonly statusBar = computed<StatusBar | undefined>(() =>
        this.getStatusBarDetails(this.isWorkStartedForToday()),
    );

    /** Whether the running week, committed and in-progress minutes summed, has hit the configured limit. */
    public readonly isWeeklyWorkHoursExceeded = computed(
        () => this.totalWeekDurationInMinutes() >= this.maxWorkingMinutes(),
    );

    public formatDate(date: moment.Moment): string {
        if (date.isSame(moment(), 'days')) {
            return <string>this.translate.instant('WEEKLY_WORK_HOUR_TODAY_LABEL');
        }
        return DateUtilities.formatDate(date);
    }

    /** A day's committed and in-progress minutes summed — the same figure the legacy row showed. */
    public dayDurationInMinutes(day: WeeklyWorkdayModel): number {
        return day.committedInMinutes + day.inProgressInMinutes;
    }

    public dayExceedsMaxHours(day: WeeklyWorkdayModel): boolean {
        return this.dayDurationInMinutes(day) >= this.maxWorkingMinutes();
    }

    public getStatusBarDetails(isWorkStartedForToday: boolean): StatusBar | undefined {
        return getStatusBarDetails(
            this.currentDayDurationInMinutes(),
            isWorkStartedForToday,
            this.translate,
            this.formatDuration,
        );
    }
}
